import {action, computed, makeObservable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {Nullable} from '../../../utils/utility-types';

import {alert} from '../../../components/alert';
import {atob} from '../../../utils/base64';
import {logger} from '../../../utils/logger';
import {platform} from '../../../utils/platform';
import {reloadApp} from '../../../utils/reloadApp';
import {storageTokenManager} from '../../../utils/storageToken';
import {type RemoteFileStorageService} from './RemoteFileStorage.service';

const DEFAULT_EXPIRATION_DATE = '3000-01-01';

export class RemoteFileStorageToken {
  constructor(
    private readonly remoteFileStorageService: RemoteFileStorageService,
  ) {
    makeObservable(this, {
      isReady: computed,
      set: action.bound,
      value: computed,
    });
  }

  get isReady() {
    if (this.shouldRunGuard()) {
      return Boolean(this.value);
    }

    return true;
  }

  get value() {
    return storageTokenManager.token;
  }

  checkIsExpired(token?: Nullable<string>) {
    if (!token) {
      return false;
    }

    if (!token.includes('.')) {
      logger.error('Invalid token format');

      return false;
    }

    // decode sign to get expiration date
    const [body] = token.split('.');

    try {
      const decodedBody = JSON.parse(atob(body)) as {exp?: number};

      const exp = decodedBody.exp
        ? new Date(decodedBody.exp * 1000)
        : new Date(DEFAULT_EXPIRATION_DATE);

      if (exp < new Date()) {
        alert.show({
          buttons: [
            {
              label: platform.isWeb
                ? i18n.t('shared:refresh')
                : i18n.t('shared:reload'),
              onClick: () => {
                reloadApp();
              },
            },
          ],
          isDismissible: true,
          message: platform.isWeb
            ? i18n.t('shared:refresh_page_to_view_document')
            : i18n.t('shared:reload_app_to_view_document'),
          title: i18n.t('shared:this_session_has_expired'),
        });

        return true;
      }

      return false;
    } catch (error) {
      if (error instanceof SyntaxError) {
        logger.error(`Failed to parse token payload:${error.message}`);
      } else {
        logger.error(error);
      }

      return false;
    }
  }

  set(token: string) {
    if (this.shouldRunGuard()) {
      storageTokenManager.setToken(token);
    }
  }

  private shouldRunGuard() {
    return this.remoteFileStorageService._shouldRunGuard();
  }
}
